import { HostListener, Inject, Injectable, DOCUMENT } from "@angular/core";
import {
    actionAccountCommandsRequest,
    actionAccountPollLiveChatRequest,
    actionAccountPollLiveChatV2Request,
    actionChangelistIdUpdate,
    actionCRUDRecordFailure,
    actionGetAccountDetails,
    actionGetAccountDetailsBillingToken,
    actionGetLocalStorageConfigServer,
    actionIndexHTMLStatics,
    actionLoadAccountRequest,
    actionLoadCartProductRequest,
    actionLoadOrdersRequest,
    actionLoadOS,
    actionLoadStationGeoIPRequest,
    actionLoadStationRequest,
    actionLoadTemplateRequest,
    actionLockRecordUpdate,
    actionPreloginResellerRequest,
    actionStationV2SocketUpdate,
    actionTwoFactorCheckAccess,
} from "../store/actions/user.actions";
import * as websocket from "websocket";
import {
    BehaviorSubject,
    from,
    fromEvent,
    interval,
    merge,
    Observable,
    of,
    share,
    Subject,
    Subscription,
    switchMap,
    throwError,
    timer,
} from "rxjs";
import { StoreSelectorsService } from "./store-selectors.service";
import {
    EnumWizardUpgradeToasts,
    ExperienceLevelType,
    FileObj,
    IAppStatic,
    IChangeListIds,
    ICRUDRecord,
    ICRUDRecordRead,
    ICRUDRecordReadReply,
    ICRUDRecordReply,
    Id,
    ILockedRecords,
    IPlayerProps,
    IPreloadResellerInfo,
    IStaticIndexHTML,
    IStationStatsPropsV1V2,
    IUser,
    PlatformStatic,
    UploadStatus,
} from "../interfaces";
import { EnumEnterpriseServerRequest, IEnterpriseReseller } from "../interfaces/enterprise-interface";
import { IAccountDetails } from "../interfaces/enterprise-account-interface";
import { IEnterpriseBusinesses } from "../interfaces/enterprise-businesses-interface";
import { actionEnterpriseRequest, actionEnterpriseRequestSocketNames } from "../store/actions/enterprise.actions";
import { bufferWhen, catchError, filter, map, skip, take, takeUntil, takeWhile, tap } from "rxjs/operators";
import { LocalStoreService } from "./local-store.service";
import {
    dateUnixToString,
    DEFAULT_LIVE_CHAT_POLL,
    DEFAULT_LIVE_CHAT_POLL_V2,
    DEFAULT_LOAD_NET_DATA,
    DEFAULT_LOAD_NET_DATA_INIT,
    DEFAULT_SHOW_SALE_AD,
    DEFAULT_STATIONS_ENTERPRISE_POLL,
    DEFAULT_STATIONS_POLL,
    DEV_TEST_PORT,
    isEmbeddedWebSite,
    isHostDevOrLocal,
    isHostLocal,
    isNullOrUndefOrEmpty,
    isSubscribersMode,
    NONE_VALUE,
    preprocessSceneRecord,
    PREVIOUSLY_USED_ACCOUNT,
    TOAST_FASTER,
    validateEmail,
    validateUrl,
    isSkipLoadingDataRoute
} from "../common";
import { TranslocoService } from "@ngneat/transloco";
import { ToastrService } from "ngx-toastr";
import { HttpClient } from "@angular/common/http";
import { PwaPromptService } from "./pwa-prompt.service";
import { Lib } from "../../libs/Lib";

import { Platform } from "@angular/cdk/platform";
import { Compbaser } from "../components/compbaser";
import { timeout } from "../decorators/timeout";
import { BreakpointObserver, BreakpointState } from "@angular/cdk/layout";
import { OnSaleComponent } from "../components/on-sale/on-sale.component";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { UserService } from "./user.service";
import { retryWithBackoff } from "../../libs/rxjs/retryWithBackoff";
import { SwUpdate, VersionEvent } from "@angular/service-worker";
// type-only: the upgrade wizard is loaded on demand (see openWizardUpgradeDialog) so it stays out
// of the shell that every page - including the equipment check-in - has to download first
import type { WizardUpgradeComponent } from "../containers/user/wizard-upgarde/wizard-upgrade.component";
// plain moment is enough here (only moment() / format are used); moment-timezone adds ~700KB of
// timezone data to every page load
import moment from "moment";
import { AppBusyService } from "./app-busy.service";
import { DialogEmailComponent } from "../components/dialog-email/dialog-email.component";
import { AccessService } from "./access-service";
import { ActionsSubject } from "@ngrx/store";
import { TwoFactorComponent } from "../containers/start/two-factor/two-factor.component";
import { ofType } from "@ngrx/effects";
import * as _ from "lodash";
import { Router } from "@angular/router";

declare var platform: PlatformStatic;

const { version: version } = require("../../../package.json");
const { onSaleNow: onSaleNow } = require("../../../package.json");

/**
 * AppService - Core Application Service
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * OVERVIEW
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * AppService is the heart of SignStudio's application architecture, managing:
 * - User authentication and session management
 * - WebSocket connections for real-time station updates
 * - Station polling and synchronization (both user and enterprise)
 * - Account data synchronization across multiple studio instances
 * - File upload management with progress tracking
 * - Resource copying for new accounts
 * - PWA (Progressive Web App) features and version management
 * - Live chat polling
 * - Network status monitoring
 * - CRUD operations for scenes and other data
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * KEY ARCHITECTURAL PATTERNS
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * 1. USER vs ENTERPRISE MODE
 *    - Regular users: Direct station polling via actionLoadStationRequest
 *    - Dashboard/Enterprise: Debounced collection polling via actionEnterpriseRequest
 *    - Check user.dashboard flag to determine mode
 *
 * 2. WEBSOCKET REAL-TIME UPDATES
 *    - Connection: wss://[businessDomain]/Web/studioWebSocket.ashx
 *    - Sends: registerStudio, pingPlayers commands
 *    - Receives: station stats (players), changelist IDs, locked records
 *    - Auto-reconnects on disconnect with 3s delay
 *
 * 3. MULTI-STUDIO SYNCHRONIZATION
 *    - Changelist IDs track data versions
 *    - WebSocket broadcasts saves from other studios
 *    - When external save detected: requestSync flag triggers syncAccount()
 *    - Locked records prevent concurrent editing conflicts
 *
 * 4. STATION POLLING STRATEGIES
 *    - Socket mode: Real-time updates via WebSocket
 *    - Poll mode: Timer-based polling (fallback)
 *    - Intervals: Regular users (DEFAULT_STATIONS_POLL), Enterprise (DEFAULT_STATIONS_ENTERPRISE_POLL)
 *    - Debouncing: Enterprise syncs use 100ms (names) / 3s (domains) delays
 *
 * 5. FILE UPLOAD ARCHITECTURE
 *    - uploadingFiles: BehaviorSubject tracking FileObj[] array
 *    - uploadingStatus: BehaviorSubject tracking aggregate progress
 *    - Statuses: READY -> INPROGRESS -> SUCCESS/FAILED
 *    - uploadFileWithProcess(): Reports progress via callback
 *    - uploadResourceFiles(): Batch uploads with retry support
 *
 * 6. NEW ACCOUNT INITIALIZATION
 *    - Server copies default resources from template account
 *    - pollResourcesReady(): Polls /Web/getResourceCount.ashx every 2s
 *    - Stops when resource count stabilizes or 50 polls (~100s)
 *    - Prevents users from seeing empty resource library
 *
 * 7. CRUD OPERATIONS
 *    - crudRecord(): Generic create/read/update/delete for all tables
 *    - Commands: "add", "update", "remove", "edit" (lock), "upload"
 *    - Tables: "player_data" (scenes), "resources", "campaigns", etc.
 *    - preprocessSceneRecord(): Converts scene objects to XML format
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * INITIALIZATION FLOW
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * constructor()
 *   ├─> initOS() - Detect browser/OS, setup debounced enterprise sync
 *   ├─> initService() - Subscribe to store, load network data
 *   └─> pollLiveChat() - Start live chat polling
 *
 * initService() [on user login]
 *   ├─> initLoadNetworkData() - Load templates, cart products
 *   ├─> initPreloadReseller() - Load white-label configuration
 *   ├─> loadIndexStatics() - Parse reseller ID from HTML
 *   ├─> Subscribe to user$ - Wait for authentication
 *   └─> [After user authenticated]
 *         ├─> initAccount() - Load account data, orders
 *         ├─> initSocket() - Establish WebSocket connection
 *         ├─> pollResourcesReady() - Wait for resource copy (new accounts)
 *         └─> initServices() - Load enterprise data (dashboard mode)
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * STATE MANAGEMENT
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * - Uses NgRx store for all state management via StoreSelectorsService
 * - Implements Compbaser for component lifecycle management
 * - Uses RxJS observables for async operations
 * - dispatchStaggered(): Staggers action dispatches to reduce server load
 * - cod(): Cleanup on destroy - auto-unsubscribes all observables
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * COMMON PITFALLS & TROUBLESHOOTING
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * 1. Stations not updating?
 *    - Check webSocketConnected flag
 *    - Verify clientSocket.readyState === OPEN
 *    - Check if station is version "2" (v1 not supported via WebSocket)
 *
 * 2. Save conflicts?
 *    - Check requestSync flag
 *    - Verify changelistIds.changelistIdSocket matches server
 *    - Check lockedRecords array for record locks
 *
 * 3. Upload not working?
 *    - Check uploadingFiles.value for FileObj status
 *    - Verify fileObj.uploadStatus !== SUCCESS before retrying
 *    - Check server FormData: formData.append("thumbnail", file)
 *
 * 4. Enterprise stations not syncing?
 *    - Verify debounce functions are initialized in initOS()
 *    - Check if debounceSyncStationsNamesEnterprise is being called
 *    - Verify user.dashboard === true
 *
 * 5. New account stuck loading?
 *    - Check accountInitialCopyResources in local storage
 *    - Verify pollResourcesReady() is running
 *    - Check server endpoint: /Web/getResourceCount.ashx
 */
@Injectable()
export class AppService extends Compbaser {
    /** Current logged-in user object with credentials and business info */
    user: IUser;

    /** Reseller configuration loaded before login (theme, branding, etc.) */
    preloginResellerInfo: IPreloadResellerInfo;

    /** Emits the new logo filename (e.g. Logo.png / Logo.jpg) right after a reseller logo
     *  upload, so the shell LogoComponent rebuilds instantly without a page refresh. */
    resellerLogoChanged$ = new Subject<string>();

    /** Array of subscriptions for station polling timers */
    pollStationSubs = [];

    /** WebSocket client for real-time station status updates */
    clientSocket: WebSocket;

    /** Static app configuration (URLs, settings, etc.) */
    appStatic: IAppStatic;

    /** Observable for window resize events */
    resizeObservable$: Observable<Event>;

    /** Subscription to window resize events */
    resizeSubscription$: Subscription;

    /** Unique identifier for this service instance */
    u = _.uniqueId();

    /** Debounced function to sync station names in enterprise mode (100ms delay) */
    debounceSyncStationsNamesEnterprise;

    /** Debounced function to sync station domains in enterprise mode (3s delay) */
    debounceSyncStationsDomainsEnterprise;

    /** Flag indicating if WebSocket connection is established */
    webSocketConnected = false;

    /** Changelist IDs for tracking data synchronization between studios */
    changelistIds: IChangeListIds;

    /** Flag indicating if account sync is needed due to external changes */
    requestSync = false;

    /** Account details including billing, contact, and shipping info */
    accountDetails: IAccountDetails;

    /** BehaviorSubject tracking files currently being uploaded */
    private uploadingFiles = new BehaviorSubject<FileObj[]>(null);

    /** BehaviorSubject tracking upload progress (percent and show flag) */
    private uploadingStatus = new BehaviorSubject(null);

    /** Subject used to stop resource polling for new accounts */
    private stopPolling = new Subject();

    /** Counter tracking resources copied during account initialization */
    private accountInitialCopyResources = 0;

    /** Expose experience level enum to templates */
    readonly experienceLevelType = ExperienceLevelType;

    /** Current user's experience level (beginner/advanced) */
    experienceLevel: ExperienceLevelType;

    /**
     * Network Status Handlers
     * These @HostListener decorators automatically trigger when browser detects network changes
     */

    /** Shows toast notification when internet connection is restored */
    @HostListener("window:online")
    setNetworkOnline(): void {
        this.toastr.info(this.translocoService.translate("general.gainedNet", {}, this.translocoService.getActiveLang()));
    }

    /** Shows error toast notification when internet connection is lost */
    @HostListener("window:offline")
    setNetworkOffline(): void {
        this.toastr.error(this.translocoService.translate("general.lostNet", {}, this.translocoService.getActiveLang()));
    }

    constructor(@Inject(DOCUMENT) private document: Document,
                private _http: HttpClient,
                private platformCDK: Platform,
                public override dialog: MatDialog,
                private actionsSubj: ActionsSubject,
                private accessService: AccessService,
                private breakpointObserver: BreakpointObserver,
                private userService: UserService,
                private storeSelector: StoreSelectorsService,
                private pwaPromptService: PwaPromptService,
                private localStoreService: LocalStoreService,
                private router: Router,
                private translocoService: TranslocoService,
                private toastr: ToastrService,
                private busyService: AppBusyService,
                private swUpdate: SwUpdate) {

        super(dialog);
        if (isSkipLoadingDataRoute()) {
            return;
        }
        this.initOS();
        this.initService();
        this.pollLiveChat();
    }

    /**
     * Initializes OS and Browser Detection
     *
     * Detects the current operating system, browser type, and version information
     * using the platform.js library. This information is stored in the NgRx store
     * and used throughout the app for browser-specific logic.
     *
     * Also initializes debounced enterprise station sync functions:
     * - debounceSyncStationsNamesEnterprise: 100ms delay for name syncs
     * - debounceSyncStationsDomainsEnterprise: 3000ms delay for domain syncs
     */
    private initOS() {
        // require('platform');
        const info: any = platform.parse(platform.ua);
        const regex = RegExp("Edg");
        // if (platform?.name?.toLowerCase() === 'safari') {
        //     alert('Safari is not supported, please use Google Chrome');
        // }
        const os = {
            info,
            isBrowser: this.platformCDK.isBrowser,
            name1: platform.name,
            name2: info.name,
            family: platform.os.family,
            description1: platform.description,
            description2: info.description,
            uaSting: platform.ua,
            isEdge: regex.test(platform.ua),
            version: info.version,
            layout: info.layout,
            os: info.os,
        };
        this.storeSelector.dispatch(actionLoadOS({ os }));

        // Debounced enterprise station sync - names (100ms delay)
        this.debounceSyncStationsNamesEnterprise = _.debounce((key) => {
            this.storeSelector
                .getBusinessesByDomain()
                .pipe(take(1))
                .subscribe((res) => {
                    this.storeSelector.dispatch(actionEnterpriseRequestSocketNames({
                        callType: EnumEnterpriseServerRequest.STATION_DOMAIN_COLLECTION_SOCKET_NAMES,
                        args: res,
                    }));
                });
        }, 100);

        this.debounceSyncStationsDomainsEnterprise = _.debounce((key) => {
            this.storeSelector
                .getBusinessesByDomain()
                .pipe(take(1))
                .subscribe((res) => {
                    this.storeSelector.dispatch(actionEnterpriseRequest({
                        callType: EnumEnterpriseServerRequest.STATION_DOMAIN_COLLECTION,
                        args: res,
                    }));
                });
        }, 3000);
    }

    /**
     * Main Service Initialization
     *
     * Called from constructor to initialize all core service functionality:
     * 1. Loads network data (templates, cart products)
     * 2. Initializes reseller configuration
     * 3. Loads static HTML index properties
     * 4. Checks for equipment redirect
     * 5. Sets up two-factor authentication listeners
     * 6. Subscribes to store changes for:
     *    - App settings
     *    - Changelist IDs (for sync)
     *    - Reseller info
     *    - User authentication
     *    - Account details
     * 7. Initializes upload status tracking
     */
    private initService() {
        this.initLoadNetworkData();
        this.initPreloadReseller();
        this.loadIndexStatics();
        this.checkEquipment();
        this.cod(
            this.actionsSubj.pipe(
                ofType(actionTwoFactorCheckAccess),
                switchMap((payload) =>
                    interval(333).pipe(
                        switchMap(() => this.storeSelector.getTwoFactorState()
                            .pipe(take(1))),
                        takeWhile((v) => v !== null && v !== true),
                        tap((v) => {
                            if (this.dialog.openDialogs.length === 0) {
                                this.dialog.open(TwoFactorComponent, {
                                    width: "400px",
                                    disableClose: true,
                                    data: payload,
                                });
                            }
                        }),
                    ),
                ),
            ).subscribe(),
        );
        this.cod(
            this.storeSelector.getAppSettings()
                .subscribe((i_appSettings: IAppStatic) => {
                    this.appStatic = i_appSettings;
                }),
        );
        this.cod(
            this.storeSelector.getChangelistIds()
                .subscribe((i_changelistIds) => {
                    this.changelistIds = i_changelistIds;
                }),
        );
        this.cod(
            this.storeSelector.getPreloginResellerInfo()
                .subscribe((i_preloginResellerInfo) => {
                    this.preloginResellerInfo = i_preloginResellerInfo;
                    // in future also add server support
                    this.localStoreService.setStorageValConfig("defaultLanguage", "en");
                    // this.localStoreService.setStorageValConfig("theme", String(i_preloginResellerInfo.theme_id));
                }),
        );
        this.cod(
            this.storeSelector.getUser()
                .pipe(
                    filter((user) => {
                        if (Lib.IsAccessBlocked(user)) {
                            setTimeout(() => {
                                this.showAccountInactiveWarning(user);
                            }, 3000);

                        }
                        setTimeout(() => {
                            this.syncUser(user);
                        }, 5000);
                        if (!user.webApiResellerBaseUrl) {
                            return false;
                        } else {
                            return true;
                        }
                    }),
                    take(1),
                )
                .subscribe((user: IUser) => {
                    if (isSubscribersMode()) {
                        this.storeSelector.dispatch(actionGetAccountDetailsBillingToken({ password: user.passwordNew }));
                        return;
                    }
                    this.initAccount(user);
                    if (!user?.dashboard) {
                        this.initSocket();
                        this.pollResourcesReady();
                    }
                    this.initServices();

                    /** future support **/
                    // this.initListenOrientation();
                    // this.initListenResize();
                    // this.initUserInactivity();
                }),
        );
        this.cod(
            this.storeSelector.getUserAccountDetails()
                .pipe(take(1))
                .subscribe((i_accountDetails: IAccountDetails) => {
                    this.accountDetails = i_accountDetails;
                    this.checkIfEmailExists();
                }),
        );
        this.setUploadingStatus({
            percent: 1,
            show: false,
        });
    }

    private findEmail(): string {
        if (validateEmail(this.accountDetails?.Account?.Shipping[0]?.$?.email)) {
            return this.accountDetails.Account.Shipping[0].$.email;
        }
        if (validateEmail(this.accountDetails?.Account?.Billing[0]?.$?.email)) {
            return this.accountDetails.Account.Billing[0].$.email;
        }
        if (validateEmail(this.accountDetails?.Account?.Contact[0]?.$?.email)) {
            return this.accountDetails.Account.Contact[0].$.email;
        }
        if (validateEmail(this.user?.username)) {
            return this.user.username;
        }
        if (validateEmail(this.user?.email)) {
            return this.user.email;
        }
        if (validateEmail(this.user?.name)) {
            return this.user.name;
        }
        return "";
    }

    private initPreloadReseller() {
        const resellerId = this.localStoreService.getItem("IPreloginReseller", "");
        if (!isNullOrUndefOrEmpty(resellerId)) {
            // this.localStoreService.setPreloginReseller(resellerId);
            this.storeSelector.dispatch(actionPreloginResellerRequest({ isWhitelabel: resellerId }));
        }
    }

    /**
     * Shows the "account inactive / update billing" warning. Public + defaults to
     * the cached user so feature gates can reuse it instead of the upgrade dialog
     * when the account is in a paid plan but not active. Single generic message.
     */
    public showAccountInactiveWarning(i_user: IUser = this.user) {
        if (!i_user) return;
        if (isEmbeddedWebSite()) return;
        return this.toastr.error(
            this.translocoService.translate("account.screenDisruption", {}, this.translocoService.getActiveLang()),
            " [ Contact live chat ]",
            {
                closeButton: true,
                disableTimeOut: true,
                positionClass: "toast-top-center",
            });
    }

    private initLoadNetworkData() {
        this.storeSelector.dispatchStaggered([
            actionLoadTemplateRequest({}),
            actionLoadCartProductRequest(""),
        ], DEFAULT_LOAD_NET_DATA_INIT, DEFAULT_LOAD_NET_DATA);
    }

    private initListenResize() {
        this.resizeObservable$ = fromEvent(window, "resize");
        this.resizeSubscription$ = this.resizeObservable$
            .subscribe((evt) => {
                console.log(`resizeEvent: ${evt.target["innerWidth"]} ${evt.target["innerHeight"]}`);
            });
    }

    private initListenOrientation() {
        this.breakpointObserver
            .observe(["(min-width: 900px)"])
            .subscribe((state: BreakpointState) => {
                if (state.matches) {
                    console.log("Viewport is 900px or over!");
                } else {
                    console.log("Viewport is smaller than 900px!");
                }
            });
        const layoutChanges = this.breakpointObserver.observe([
            "(orientation: portrait)",
            "(orientation: landscape)",
        ]);
        layoutChanges.subscribe(result => {
            console.log("orient change");
        });
    }

    private initUserInactivity() {
        const perMinute$ = interval(60000_000);
        const events$ = merge(
            fromEvent(document.body, "click"),
            fromEvent(document.body, "mousemove"),
            fromEvent(document.body, "scroll"),
        );
        this.cod(
            events$.pipe(
                bufferWhen(() => perMinute$),
                filter(events => events.length === 0),
            ).subscribe(() => {
                console.log("You have been inactive for a minute, logging out...");
            }),
        );
    }

    private initServices() {
        /** Dashboard **/
        this.cod(
            this.storeSelector.getEnterpriseReseller()
                .pipe(take(1))
                .subscribe((enterprise: IEnterpriseReseller) => {
                    this.storeSelector.dispatch(actionEnterpriseRequest({ callType: EnumEnterpriseServerRequest.ACCOUNT_DETAILS }));
                }),
        );
        this.cod(
            this.storeSelector.getEnterpriseAccDetails()
                .pipe(take(1))
                .subscribe((enterprise: IAccountDetails) => {
                    this.storeSelector.dispatch(actionEnterpriseRequest({ callType: EnumEnterpriseServerRequest.BUSINESSES_INFO }));
                }),
        );
        this.cod(
            this.storeSelector.getEnterpriseBusinesses()
                .pipe(take(1))
                .subscribe((enterprise: IEnterpriseBusinesses) => {
                    this.debounceSyncStationsNamesEnterprise();
                    this.debounceSyncStationsDomainsEnterprise();
                    this.storeSelector.dispatch(actionEnterpriseRequest({
                        callType: EnumEnterpriseServerRequest.BUSINESS_USERS,
                        args: enterprise.Businesses.BusinessInfo,
                    }));
                }),
        );
    }

    private initAccount(i_user: IUser) {
        this.user = i_user;
        /** Dashboard **/
        if (this?.user?.dashboard) {
            this.storeSelector.dispatchStaggered([
                actionEnterpriseRequest({ callType: EnumEnterpriseServerRequest.RESELLER_INFO }),
                actionLoadOrdersRequest({}),
            ], 333, 2500);
        } else {
            /** User **/
            this.storeSelector.dispatchStaggered([
                actionGetLocalStorageConfigServer({ val: this.user }),
                actionLoadAccountRequest({ businessId: this.user.businessId, changelistId: NONE_VALUE }),
                actionGetAccountDetails(""),
                actionLoadOrdersRequest({}),
            ], 333, 2000);
            this.syncUser(this.user);
            this.syncStationsUser();
        }
        // this.storeSelector.dispatchStaggered([
        //     actionLoadTrackingStationRequest({globalDataRequest: null}),
        //     actionLoadTrackingStationStatsRequest({globalDataRequest: null})
        // ], 1500, 2000);
    }

    /** get extra station data as Geo stats and tracking details **/
    private getStationsExtras() {
        this.cod(
            this.storeSelector.getStationStats()
                .pipe(
                    // filter((v) => v.length !== 0),
                    // delay(1000),
                    take(1),
                ).subscribe((v) => {
                if (this?.user?.dashboard) {
                    this.storeSelector.dispatch(actionEnterpriseRequest({
                        callType: EnumEnterpriseServerRequest.STATION_COLLECTION_GEOIP,
                        args: [],
                    }));
                } else {
                    this.storeSelector.dispatch(actionLoadStationGeoIPRequest({}));
                }
            }),
        );
    }

    private checkEquipment() {
        if (!this.localStoreService) return;
        if (this.localStoreService.getStorageConfig()?.equipmentPwa === "1") {
            console.log("redirecting to /shopping-cart/equipment");
            this.router.navigateByUrl("/shopping-cart/equipment");
        }
    }

    private loadIndexStatics() {
        const staticIndexHTML: IStaticIndexHTML = { language: null, resellerStaticId: null };
        try {
            staticIndexHTML.resellerStaticId = Number(this.document.head.querySelector("[property~=enterprise][id]")["id"]);
        } catch (e) {
        }
        try {
            staticIndexHTML.language = this.document.head.querySelector("[property~=language][id]")["id"];
        } catch (e) {
        }
        this.storeSelector.dispatch(actionIndexHTMLStatics({ staticIndexHTML }));
        // if wanted to load it from package json instead
        // this._http.get<any>(`${this.appSettings.globalService}/${HOST_DIR}/package.json`).pipe(take(1)).subscribe((r) => {
        //     console.log(`reseller id: ${r.resellerId}`);
        // });
    }

    /** Latest available SignPlayer version, read from the deployed player's updateInfo.json
     *  (served at <origin>/installplayer/updateInfo.json, same host as Studio). Used to flag
     *  players whose reported version is behind. Returns '' on any error (no flag then). */
    getLatestPlayerVersion(): Observable<string> {
        const url = `${window.location.origin}/installplayer/updateInfo.json?t=${Date.now()}`;
        return this._http.get<any>(url).pipe(
            take(1),
            map((info) => (info && info.version) ? String(info.version) : ""),
            catchError(() => of("")),
        );
    }

    private syncStationsUser() {
        if (!this.user) return;
        this.storeSelector.dispatch(actionLoadStationRequest({ businessId: this.user.businessId }));
    }

    @timeout(1000)
    public reloadBusinessUser() {
        this.storeSelector.getEnterpriseBusinesses()
            .pipe(skip(1), take(1))
            .subscribe((enterprise: IEnterpriseBusinesses) => {
                this.storeSelector.dispatch(actionEnterpriseRequest({
                    callType: EnumEnterpriseServerRequest.BUSINESS_USERS,
                    args: enterprise.Businesses.BusinessInfo,
                }));
            });
        this.storeSelector.dispatch(actionEnterpriseRequest({ callType: EnumEnterpriseServerRequest.BUSINESSES_INFO }));
    }

    public pwaOpenInstaller() {
        const pwaHideInstaller = this.localStoreService.getStorageValConfig("pwaHideInstaller");
        if (pwaHideInstaller === "1") return;
        this.experienceLevel = this.localStoreService.getStorageValConfig("experienceLevel"); // Alon
        if (this.experienceLevel == ExperienceLevelType.BEGINNER) return;
        this.pwaPromptService.pwaAutoOpenPrompt();
    }

    public initOnSale() {
        timer(DEFAULT_SHOW_SALE_AD)
            .pipe(take(1))
            .subscribe(time => {
                if (Lib.DevMode()) return;
                if (!this.user || this.user.enterprise === true) return;
                if (onSaleNow.length < 2 || onSaleNow === this.localStoreService.getStorageConfig().onSaleShown) return;
                this.openOnSaleDialog();
            });
    }

    public openOnSaleDialog() {
        // width is a desktop target only - the panel class turns the dialog into a
        // full screen sheet on phones (see .on-sale-dialog-responsive in styles.scss)
        this.dialog.open(OnSaleComponent, {
            width: "1200px",
            maxWidth: "96vw",
            maxHeight: "92vh",
            panelClass: "on-sale-dialog-responsive",
            autoFocus: false,
        });
    }

    private checkIfEmailExists() {
        if (isNullOrUndefOrEmpty(this.user)) return;
        const email = this.findEmail();
        if (!isNullOrUndefOrEmpty(email)) return;
        if (Lib.GetAccountVersion(this.user) === "FREE") {
            this.openEmailDialog();
        } else {
            this.cod(
                this.accessService.getGroupsCollection("SignStudio Dashboard")
                    .pipe(map((value) => value))
                    .pipe(take(1),
                        map((value) => {
                            return value?.$?.resellerCollectUserEmail ? value.$.resellerCollectUserEmail : "0";
                        }),
                    ).subscribe((v) => {
                    if (v === "1") {
                        this.openEmailDialog();
                    }
                }),
            );
        }
    }

    private openEmailDialog() {
        const dialogRef = this.dialog.open(DialogEmailComponent, {
            width: "400px",
        });
        dialogRef.afterClosed().subscribe(result => {
            if (result) {

            }
        });
    }

    /** sync user details **/
    public syncUser(i_user?: IUser) {
        const userAccount = i_user ? i_user : this.user;
        if (isNullOrUndefOrEmpty(userAccount?.username)) return;
        if (userAccount.username === "none") return;
        this.storeSelector.appStore.dispatch(actionAccountCommandsRequest({
                accountRequest: {
                    verifyAccount: {
                        userName: userAccount.username,
                        password: userAccount.passwordNew,
                        rememberMe: userAccount.rememberMe,
                    },
                    serverCall: "SYNC_USER",
                },
            }),
        );
    }

    /**
     * Poll Resources Ready for New Accounts
     *
     * When a fresh account is created, the server copies default resources (images, videos, etc.)
     * from a template account to the new account. This can take several seconds on AWS/server side.
     *
     * This method polls the server every 2 seconds to check the resource count. Once the count
     * stabilizes (no change) or 50 polls have passed (~100 seconds), we know resources are ready.
     *
     * Flow:
     * 1. Check local storage for accountInitialCopyResources value
     * 2. If value is "-1" (PREVIOUSLY_USED_ACCOUNT), skip polling - account already initialized
     * 3. Otherwise, poll server endpoint: /Web/getResourceCount.ashx
     * 4. Compare count on each poll - if unchanged, resources are copied
     * 5. After stable or timeout, mark account as ready by setting value to "-1"
     *
     * Note: This prevents users from encountering missing resources on first login
     */
    private pollResourcesReady() {
        // this.localStoreService.setStorageValConfig('accountInitialCopyResources', '0');
        // return;
        const initAccountResourcesCount = this.localStoreService.getStorageConfig().accountInitialCopyResources;
        /** if -1 it is a previously used account so no need to check server copy on resources **/
        if (initAccountResourcesCount === PREVIOUSLY_USED_ACCOUNT) return;
        let counter = 0;
        const url = `${this.user.webApiBaseUrl}/Web/getResourceCount.ashx?businessId=${this.user.businessId}`;
        const r$ = timer(1, 2000)
            .pipe(
                switchMap((a) => this._http.get<any[]>(url)),
                retryWithBackoff(url, this.storeSelector, this.toastr),
                catchError((err) => {
                    console.error("accountInitialCopyResources error " + err);
                    // this.toastr.error('No resources, is this a Studio legacy account?');
                    return throwError(err);
                }),
                share(),
                takeUntil(this.stopPolling),
            ).subscribe((v: any) => {
                counter++;
                /** if no change in total resources copied on sever means the account is ready, or if too much time has passed account is ready **/
                if (counter > 50 || v?.count === this.accountInitialCopyResources) {
                    setTimeout(() => {
                        this.stopPolling.next(null);
                        this.localStoreService.setStorageValConfig("accountInitialCopyResources", PREVIOUSLY_USED_ACCOUNT);
                    }, 7000);
                } else {
                    this.accountInitialCopyResources = v.count;
                    this.localStoreService.setStorageValConfig("accountInitialCopyResources", this.accountInitialCopyResources);
                }
            });
    }

    /**
     * Initialize WebSocket Connection
     *
     * Establishes a WebSocket connection to the user's business domain for real-time updates.
     * This WebSocket is used for:
     * 1. Real-time station status updates (connection, playing, watchdog, version, IP, etc.)
     * 2. Multi-studio synchronization (changelist IDs, record locking)
     * 3. Detecting when other studios save changes (triggers account sync)
     * 4. Sending ping commands to stations
     *
     * WebSocket Messages Handled:
     * - players: Array of station status updates (v2 stations only)
     * - studios: Changelist ID and locked records for multi-studio sync
     * - sender: Studio ID that sent the message (to detect external changes)
     *
     * Connection Flow:
     * 1. Connect to wss://[businessDomain]/Web/studioWebSocket.ashx
     * 2. On open: Send registerStudio command with businessId and studioId
     * 3. On message: Update station stats and/or sync account if needed
     * 4. On error/close: Reset stations, teardown, and reconnect after 3s
     */
    private initSocket() {
        
        if (isHostDevOrLocal() && this.user.businessDomain === "ida.signage.me") {
            this.clientSocket = new websocket.w3cwebsocket(`wss://ida.signage.me:${DEV_TEST_PORT}/Web/studioWebSocket.ashx`);
        } else {
            this.clientSocket = new websocket.w3cwebsocket(`wss://${this.user.businessDomain}/Web/studioWebSocket.ashx`);
        }

        this.clientSocket.onerror = (err) => {
            this.resetStationsSockets();
            this.stationSocketTeardown();
            Lib.DevShow("ws: err " + err);
        };

        this.clientSocket.onopen = () => {
            // Lib.DevShow('ws: connected');
            if (this.clientSocket.readyState === this.clientSocket.OPEN) {
                this.resetStationsSockets();
                const msg = {
                    businessId: this.user.businessId,
                    studioId: this.localStoreService.getStorageConfig().studioId,
                    command: "registerStudio",
                };
                this.clientSocket.send(JSON.stringify(msg));
            }
        };

        this.clientSocket.onclose = () => {
            this.resetStationsSockets();
            this.stationSocketTeardown();
        };

        this.clientSocket.onmessage = (e) => {
            let socketReply;
            if (typeof e.data === "string") {
                // Lib.DevShow('ws: ' + e.data);
                try {
                    socketReply = JSON.parse(e.data);
                    if (socketReply && socketReply.players && socketReply.players.length > 0) {
                        socketReply.players.forEach((_player: IStationStatsPropsV1V2) => {
                            const value: IStationStatsPropsV1V2 = {
                                connection: _player.connected,
                                playing: _player?.playing,
                                wdConnected: _player?.wdConnected,
                                wdNative: _player?.wdNative,
                                wdExtension: _player?.wdExtension,
                                iotVer: _player?.iotVer,
                                libVersion: _player?.libVersion,
                                appVersion: _player?.appVersion,
                                localIp: _player?.userHostAddress,
                                platform: _player?.platform,
                                // System metrics from SignService
                                cpuUsage: _player?.cpuUsage,
                                memoryUsage: _player?.memoryUsage,
                                memoryTotal: _player?.memoryTotal,
                                temperature: _player?.temperature,
                                gpuTemperature: _player?.gpuTemperature,
                                diskUsed: _player?.diskUsed,
                                diskTotal: _player?.diskTotal,
                                uptime: _player?.uptime,
                                wifiSignal: _player?.wifiSignal,
                                osVersion: _player?.osVersion,
                                processCount: _player?.processCount,
                                networkDown: _player?.networkDown,
                                networkUp: _player?.networkUp,
                                deviceLocalIp: _player?.deviceLocalIp,
                                macAddress: _player?.macAddress,
                            };

                            // Only include BLE fields when they have actual values
                            // to prevent clearing existing BLE data on regular status updates
                            if (_player?.bleDevices) value.bleDevices = _player.bleDevices;
                            if (_player?.bleConnectedDevices) value.bleConnectedDevices = _player.bleConnectedDevices;
                            if (_player?.bleServices) value.bleServices = _player.bleServices;
                            if (_player?.bleConnectResult) value.bleConnectResult = _player.bleConnectResult;
                            if (_player?.bleDisconnectResult) value.bleDisconnectResult = _player.bleDisconnectResult;
                            if (_player?.bleReadResult) value.bleReadResult = _player.bleReadResult;
                            if (_player?.bleWriteResult) value.bleWriteResult = _player.bleWriteResult;
                            if (_player?.bleNotification) value.bleNotification = _player.bleNotification;
                            if (_player?.bleRssi) value.bleRssi = _player.bleRssi;

                            // IoT dependencies status
                            if (_player?.iotDepsStatus) value.iotDepsStatus = _player.iotDepsStatus;
                            if (_player?.iotDepsInstallResult) value.iotDepsInstallResult = _player.iotDepsInstallResult;
                            if (_player?.iotDepsUpdateResult) value.iotDepsUpdateResult = _player.iotDepsUpdateResult;
                            // Zigbee dongle data
                            if (_player?.zigbeeDongles) value.zigbeeDongles = _player.zigbeeDongles;
                            if (_player?.zigbeeStatus) value.zigbeeStatus = _player.zigbeeStatus;
                            if (_player?.zigbeeDevices) value.zigbeeDevices = _player.zigbeeDevices;
                            if (_player?.zigbeeDeviceJoined) value.zigbeeDeviceJoined = _player.zigbeeDeviceJoined;
                            if (_player?.zigbeeDeviceUpdated) value.zigbeeDeviceUpdated = _player.zigbeeDeviceUpdated;
                            if (_player?.zigbeePairingStatus) value.zigbeePairingStatus = _player.zigbeePairingStatus;
                            if (_player?.zigbeePairingUpdate) value.zigbeePairingUpdate = _player.zigbeePairingUpdate;
                            if (_player?.zigbeeDeviceEvent) value.zigbeeDeviceEvent = _player.zigbeeDeviceEvent;
                            if (_player?.zigbeeCommandResult) value.zigbeeCommandResult = _player.zigbeeCommandResult;
                            if (_player?.zigbeeReadResult) value.zigbeeReadResult = _player.zigbeeReadResult;

                            // KNX data
                            if (_player?.knxStatus) value.knxStatus = _player.knxStatus;
                            if (_player?.knxGateways) value.knxGateways = _player.knxGateways;

                            // HA data
                            if (_player?.haStatus) value.haStatus = _player.haStatus;
                            if (_player?.haGateways) value.haGateways = _player.haGateways;

                            // Customer API data
                            if (_player?.apiStatus) value.apiStatus = _player.apiStatus;
                            if (_player?.apiTestResult) value.apiTestResult = _player.apiTestResult;
                            if (_player?.apiEvent) value.apiEvent = _player.apiEvent;

                            // HEOS data
                            if (_player?.heosStatus) value.heosStatus = _player.heosStatus;
                            if (_player?.heosSpeakers) value.heosSpeakers = _player.heosSpeakers;
                            if (_player?.heosPlayers) value.heosPlayers = _player.heosPlayers;
                            if (_player?.heosConnectResult) value.heosConnectResult = _player.heosConnectResult;
                            if (_player?.heosSetVolumeResult) value.heosSetVolumeResult = _player.heosSetVolumeResult;
                            if (_player?.heosSetMuteResult) value.heosSetMuteResult = _player.heosSetMuteResult;
                            if (_player?.heosSetPlayStateResult) value.heosSetPlayStateResult = _player.heosSetPlayStateResult;
                            if (_player?.heosPlayerVolumeChanged) value.heosPlayerVolumeChanged = _player.heosPlayerVolumeChanged;
                            if (_player?.heosPlayerStateChanged) value.heosPlayerStateChanged = _player.heosPlayerStateChanged;
                            if (_player?.heosNowPlayingChanged) value.heosNowPlayingChanged = _player.heosNowPlayingChanged;

                            // Display monitor on/off state
                            if (_player?.monitorState) value.monitorState = _player.monitorState;

                            // Connected-display count/outputs (one-big-screen feature). SP sends a
                            // JSON string {count, outputs}; keep the raw value and a parsed count
                            // (count drives the SCREEN column: 1 vs 2 monitor icons).
                            if (_player?.displays) {
                                value.displays = _player.displays;
                                try {
                                    const d = typeof _player.displays === 'string' ? JSON.parse(_player.displays) : _player.displays;
                                    value.displaysCount = d?.count ?? 0;
                                } catch { }
                            }

                            // Schedule capabilities (gates UI for power_mode 1 / 2)
                            if (_player?.scheduleCapabilities) value.scheduleCapabilities = _player.scheduleCapabilities;

                            // Player-reported sync verdict ("1"/"0") — drives the grid "sync" column.
                            // Use != null (not truthy) so "0" (out-of-sync) is not dropped.
                            if (_player?.inSync != null) value.inSync = _player.inSync;

                            // GPIO data
                            if (_player?.gpioPins) value.gpioPins = _player.gpioPins;
                            if (_player?.gpioReadResult) value.gpioReadResult = _player.gpioReadResult;
                            if (_player?.gpioWriteResult) value.gpioWriteResult = _player.gpioWriteResult;
                            if (_player?.gpioSetPwmResult) value.gpioSetPwmResult = _player.gpioSetPwmResult;
                            if (_player?.gpioEvent) value.gpioEvent = _player.gpioEvent;
                            if (_player?.gpioDhtReadResult) value.gpioDhtReadResult = _player.gpioDhtReadResult;

                            if (_player?.startTime && !_.isNull(parseInt(_player?.startTime))) {
                                value["startTime"] = dateUnixToString(_player?.startTime, -3, "MM-DD-YY HH:mm:ss");
                            }
                            if (_player?.lastUpdate && !_.isNull(parseInt(_player?.lastUpdate))) {
                                const now = moment().unix();
                                const last = Number(_player?.lastUpdate);
                                const final: any = now - last;
                                value["lastUpdate"] = final;
                                // value['lastUpdate'] = moment(_player.lastUpdate).format('MM-DD-YY HH:mm:ss');
                            }
                            this.storeSelector.dispatchStaggered([actionStationV2SocketUpdate({
                                id: _player.playerId,
                                businessId: this.user.businessId,
                                version: "2",
                                value,
                            })], 0, 1000);
                        });
                    }
                    if (!socketReply.studios) {
                        return;
                    }
                } catch (e) {
                    this.toastr.error(this.translocoService.translate("general.problemSocket", {}, this.translocoService.getActiveLang()));
                    return;
                }

                /** save interrupted by another user so we re-sync account data so we can merge it  **/
                if (socketReply.sender !== this.localStoreService.getStorageConfig().studioId) {
                    if (this.changelistIds.changelistIdSocket !== socketReply.studios.changelistId) {
                        this.requestSync = true;
                        this.syncAccount();
                    }
                } else {
                    /** this studio saves **/
                    this.requestSync = false;
                    this.storeSelector.dispatch(actionChangelistIdUpdate({ payload: { changelistId: socketReply.studios.changelistId } }));
                }

                if (socketReply.studios != null) {
                    const lockedRecords: Array<ILockedRecords> = [];
                    Object.keys(socketReply.studios.lockedRecords).forEach((k) => {
                        const lockId = socketReply.studios.lockedRecords[k];
                        const v = k.split(".");
                        lockedRecords.push({
                            lockId,
                            tableId: v[1],
                            table: v[0],
                        });
                    });
                    this.storeSelector.dispatch(actionLockRecordUpdate({
                        payload: {
                            changelistId: socketReply.studios.changelistId,
                            lockedRecords,
                        },
                    }));
                }
            }
        };

        this.webSocketConnected = true;
    }

    private stationSocketTeardown() {
        console.log("station socket teardown, reconnecting...");
        this.webSocketConnected = false;
        if (this.clientSocket) {
            this.clientSocket.close();
            this.clientSocket.removeAllListeners();
        }
        this.clientSocket = null;
        setTimeout(() => {
            this.initSocket();
        }, 3000);
    }

    /**
     * Sync Account Data
     *
     * Called when WebSocket detects that another studio has made changes.
     * Re-loads account data and stations with a staggered delay to avoid
     * overwhelming the server with simultaneous requests.
     *
     * Uses changelistIdSync to fetch only changes since last sync.
     */
    public syncAccount() {
        this.storeSelector.dispatchStaggered([
            actionLoadAccountRequest({
                businessId: this.user.businessId,
                changelistId: this.changelistIds.changelistIdSync,
            }),
            actionLoadStationRequest({ businessId: this.user.businessId }),
        ], null, 2000);
    }

    /** Fire a one-off command to a single player via sendCommand.ashx. Version-aware: SignPlayer
     *  (v2) uses the POST /Web path, legacy (v1) uses the JSONP /WebService path — mirroring
     *  station-props.sendCommand(). Fire-and-forget. `version` should be the player's version
     *  ('2' = Sign/PWA, anything else = legacy); it defaults to v2 for backward compatibility.
     *  Used by the players grid for 'checkSync' and by the UPDATE button for 'checkUpdates'. */
    public sendPlayerCommand(stationId: string, command: string, version: string | number = '2'): void {
        const req = this.buildPlayerCommand$(stationId, command, version);
        if (req) req.subscribe({ next: () => {}, error: () => {} });
    }

    /** Same as sendPlayerCommand but returns the request as an Observable so callers can await it
     *  (e.g. to disable a button / uncheck rows once the command has actually been sent). Never
     *  throws — errors resolve to null so a forkJoin over several players still completes. */
    public sendPlayerCommand$(stationId: string, command: string, version: string | number = '2'): Observable<any> {
        return this.buildPlayerCommand$(stationId, command, version) || of(null);
    }

    /** Build the version-aware sendCommand.ashx request (not subscribed). Returns null when there is
     *  no user/stationId. v2 → POST /Web with the command in the body; legacy → JSONP GET /WebService
     *  with the command in the query (i_command), matching the legacy branch of sendCommand(). */
    private buildPlayerCommand$(stationId: string, command: string, version: string | number): Observable<any> | null {
        if (!this.user || !stationId) return null;
        const email = this.user.email || this.user.name;
        const pass = Lib.Base64().encode(this.user.passwordNew);
        if (String(version) === '2') {
            const url = `${this.user.webApiBaseUrl}/Web/sendCommand.ashx?user=${email}&pass=${pass}&stationId=${stationId}&base64=1`;
            return this._http.post(url, { command }).pipe(catchError(() => of(null)));
        }
        // legacy (v1): JSONP GET, command carried in the query string.
        const url = `${this.user.webApiBaseUrl}/WebService/sendCommand.ashx?i_user=${email}&i_password=${pass}&i_stationId=${stationId}&i_command=${command}&i_param1='SignageStudioLite'&i_param2=''&base64=1`;
        return this._http.jsonp(url, 'callback').pipe(catchError(() => of(null)));
    }

    /**
     * Sync Stations - Universal Entry Point
     *
     * Intelligently routes to correct sync method based on user type:
     * - Dashboard users: syncStationsEnterprise() with debouncing
     * - Regular users: syncStationsUser() direct load
     */
    public syncStations() {
        if (this?.user?.dashboard) {
            this.syncStationsEnterprise();
        } else {
            this.syncStationsUser();
        }
    }

    /**
     * Sync Stations for Enterprise Dashboard Users
     *
     * Uses debounced functions to prevent excessive API calls:
     * - syncStationsNamesEnterprise: 100ms debounce for station name updates
     * - syncStationsDomainsEnterprise: 3s debounce for domain collection updates
     *
     * These are debounced because enterprise accounts can have many businesses
     * and stations, and frequent updates could overwhelm the server.
     */
    public syncStationsEnterprise() {
        this.debounceSyncStationsNamesEnterprise();
        this.debounceSyncStationsDomainsEnterprise();
    }

    public stopPollStations() {
        this.pollStationSubs.forEach((sub) => {
            sub.unsubscribe();
        });
    }

    public stationSocketPing() {
        try {
            const msg = {
                businessId: this.user.businessId,
                studioId: this.localStoreService.getStorageConfig().studioId,
                command: "pingPlayers",
            };
            if (!this.clientSocket ||
                this.clientSocket.readyState === this.clientSocket.CONNECTING ||
                this.clientSocket.readyState === this.clientSocket.CLOSED) {
                return;
            }
            this.clientSocket.send(JSON.stringify(msg));
        } catch (err) {
            console.error("problem sending clientSocket " + err);
        }
    }

    public goToHomePage(popUp: boolean = false) {
        let url;
        if (this.user?.enterprise) {
            url = `${this.user?.customStudio?.Studio?.Application?.[0]?.Links?.[0]?.$?.home}`;
        } else if (this?.preloginResellerInfo) {
            url = `${this.preloginResellerInfo?.custom_studio_json?.Application?.[0]?.Links?.[0]?.$?.home}`;
        } else {
            url = this.appStatic.homePage;
        }
        url = validateUrl(url);
        if (url === null) {
            url = this.appStatic.homePage;
        }
        if (popUp) {
            window.open(url, "_blank");
        } else {
            window.location.href = url;
        }
    }

    // emulation mode only for socket stations version 2
    public socketStations() {
        const TIMEOUT = this?.user?.dashboard ? DEFAULT_STATIONS_ENTERPRISE_POLL : DEFAULT_STATIONS_POLL;
        const s = timer(1000, TIMEOUT)
            .subscribe(val => {
                if (this?.user?.dashboard) {
                    this.debounceSyncStationsNamesEnterprise();
                    this.debounceSyncStationsDomainsEnterprise();
                } else {
                    this.syncStationsUser();
                }
                this.getStationsExtras();
            });
        this.pollStationSubs.push(s);
    }

    public pollStations() {
        let TIMEOUT = DEFAULT_STATIONS_POLL;
        if (this?.user?.dashboard) {
            TIMEOUT = DEFAULT_STATIONS_ENTERPRISE_POLL;
        }
        const s = timer(2000, TIMEOUT)
            .subscribe(val => {
                if (this?.user?.dashboard) {
                    this.debounceSyncStationsNamesEnterprise();
                    this.debounceSyncStationsDomainsEnterprise();
                } else {
                    this.syncStationsUser();
                }
                this.getStationsExtras();
            });
        this.pollStationSubs.push(s);
    }

    public pollLiveChat() {
        const s1 = timer(1000, DEFAULT_LIVE_CHAT_POLL).subscribe(val => {
            this.storeSelector.dispatch(actionAccountPollLiveChatRequest(""));
        });
        const s2 = timer(1, DEFAULT_LIVE_CHAT_POLL_V2).subscribe(val => {
            this.storeSelector.dispatch(actionAccountPollLiveChatV2Request(""));
        });
    }

    public checkAppVersion(): Promise<VersionEvent> {
        return new Promise((resolve, reject) => {
            if (isHostLocal()) return resolve(null);
            console.log("checking for latest version studio");
            this.swUpdate.versionUpdates.subscribe((evt) => {
                console.log("UpdateService: versionUpdates", evt);
                switch (evt.type) {
                    case "VERSION_DETECTED":
                        console.log(`Downloading new app version: ${evt.version.hash}`);
                        break;
                    case "VERSION_READY":
                        console.log(`Current app version: ${evt.currentVersion.hash}`);
                        console.log(`New app version ready for use: ${evt.latestVersion.hash}`);
                        resolve(evt);
                        break;
                    case "VERSION_INSTALLATION_FAILED":
                        console.log(`Failed to install app ver '${evt.version.hash}': ${evt.error}`);
                        reject(`Failed to install app ver '${evt.version.hash}': ${evt.error}`);
                        break;
                }
            });
        });
    }

    showReleaseNotes() {
        // lazy: the release notes dialog is only needed when an update is available.
        // busyService.run shows the global loader while the chunk downloads.
        this.busyService.run(() => import("../components/dialog-version/dialog-release-notes.component")).then(({ DialogReleaseNotesComponent }) => {
            const ref = this.dialog.open(DialogReleaseNotesComponent, {
                maxWidth: "800px",
                maxHeight: "90vh",
                width: "95vw",
                panelClass: "version-dialog-responsive",
                data: null,
            });
            ref.afterClosed().subscribe(async (v) => {
                if (v) {
                    await this.activateUpdate();
                }
            });
        }).catch((e) => console.error("failed to load release notes dialog", e));
    }

    public async activateUpdate() {
        await this.swUpdate.activateUpdate();
        location.reload();
    }

    unCacheApp(): Observable<any> {
        console.log("checking sw-cache");
        if ("caches" in window) {
            return from(caches.keys()
                .then((keyList) => {
                    return Promise.all(keyList.map((key) => {
                        console.log("deleting cache " + key);
                        return caches.delete(key);
                    }));
                }),
            );
        } else {
            return of(false);
        }
    }

    unregisterSW(): Observable<any> {
        return new Observable<any>(sub => {
            if (window.navigator && navigator.serviceWorker) {
                navigator.serviceWorker.getRegistrations()
                    .then((registrations) => {
                        for (const registration of registrations) {
                            console.log("unregistering service worker");
                            registration["unregister"]();
                        }
                        console.log("unregisterSW finish");
                        sub.next(true);
                        sub.complete();
                    });
            } else {
                sub.next(false);
                sub.complete();
            }
        });
    }


    public installPwa() {
        this.pwaPromptService.pwaManuallyOpenPrompt();
    }

    /**
     * CRUD Record - Create/Read/Update/Delete Operations
     *
     * Universal method for performing CRUD operations on database tables.
     * Wraps userService.crudRecord() with promise-based interface.
     *
     * @param saveRecord - ICRUDRecord object containing:
     *   - studioId: Current studio identifier
     *   - command: "add", "update", "remove", "edit" (edit = lock record)
     *   - table: Database table name (e.g., "player_data", "resources")
     *   - primary: Primary key column name (e.g., "player_data_id")
     *   - body: Data to save (object or FormData for uploads)
     *   - id: Record ID for update/remove operations
     *   - options: Optional settings (noStringify, reportProgress)
     *
     * @returns Promise<ICRUDRecord> with serverReply containing:
     *   - status: true/false for success/failure
     *   - id: New/updated record ID
     *
     * Used for:
     * - Saving scenes (player_data table)
     * - Uploading resources (resources table)
     * - Locking records for editing
     * - Updating campaign/timeline data
     */
    public async crudRecord(saveRecord: ICRUDRecord): Promise<ICRUDRecord> {
        return new Promise((res, err) => {
            this.userService.crudRecord(saveRecord)
                .pipe(
                    map((i_reply: ICRUDRecordReply) => {
                        saveRecord.command = "reply";
                        saveRecord.serverReply = i_reply;
                        return res(saveRecord);
                    }),
                    catchError((i_error) => {
                        console.log(`actionCRUDRecordFailure ${i_error.message}`);
                        saveRecord.command = "reply";
                        saveRecord.serverReply = { id: null, status: false };
                        err(saveRecord);
                        return of(actionCRUDRecordFailure({
                            saveRecord: {
                                studioId: null,
                                command: "reply",
                                body: i_error,
                                serverReply: { status: false, id: null },
                            },
                        }));
                    }),
                ).subscribe();
        });
    }

    /**
     * CRUD Record Read - Read Records from Database
     *
     * Fetches records from database tables without modifying them.
     *
     * @param readRecord - ICRUDRecordRead object containing:
     *   - table: Database table name
     *   - primary: Primary key column name
     *   - id: Record ID to read (optional - omit for all records)
     *
     * @returns Promise<ICRUDRecordReadReply> with record data
     *
     * Used for fetching specific records or collections.
     */
    public async crudRecordRead(readRecord: ICRUDRecordRead): Promise<ICRUDRecordReadReply> {
        return new Promise((res, err) => {
            this.userService.crudRecordReadCall(readRecord)
                .pipe(
                    map((i_reply) => {
                        // console.log(i_reply);
                        // saveRecord.command = "reply";
                        // saveRecord.serverReply = i_reply;
                        return res(i_reply);
                    }),
                    catchError((i_error) => {
                        console.log(`actionCRUDRecordReadFailure ${i_error.message}`);
                        // saveRecord.command = "reply";
                        // saveRecord.serverReply = {id: null, status: false};
                        err(readRecord);
                        return of(actionCRUDRecordFailure({
                            saveRecord: {
                                studioId: null,
                                command: "reply",
                                body: i_error,
                                serverReply: { status: false, id: null },
                            },
                        }));
                    }),
                ).subscribe();
        });
    }

    /**
     * Update Multiple Scenes
     * Batch updates multiple scenes to the database. Used when changes affect
     * multiple scenes simultaneously (e.g., global palette changes, font updates).
     *
     * @param sceneIds - Array of scene IDs to update
     * @param showMessage - Whether to show success/failure toast notifications
     *
     * Flow:
     * 1. Iterates through each scene ID
     * 2. Fetches scene from store
     * 3. Preprocesses scene record (converts to XML format)
     * 4. Calls crudRecord() to save to database
     * 5. Shows toast notification if showMessage is true
     *
     * Note: Each scene is saved independently - if one fails, others still proceed
     */
    public updateMultiScene(sceneIds: Id[], showMessage = null) {
        if (!sceneIds || sceneIds.length === 0) return;
        sceneIds.forEach((_sceneId) => {
            this.storeSelector.getScene(_sceneId).pipe(take(1)).subscribe((scene: IPlayerProps) => {
                if (!scene) return;
                const saveRecord = preprocessSceneRecord(scene, _sceneId, this.localStoreService.getStorageConfig().studioId, "update");
                this.crudRecord(saveRecord).then((i_crudReply: ICRUDRecord) => {
                    if (showMessage) {
                        if (!i_crudReply.serverReply.status) {
                            this.toastr.error(this.translocoService.translate("toast.failedSave", {}, this.translocoService.getActiveLang()));
                        } else {
                            this.toastr.info(this.translocoService.translate("toast.successSave", {}, this.translocoService.getActiveLang()), "", TOAST_FASTER);
                        }
                    }
                });
            });
        });
    }

    public resetStationsSockets() {
        this.storeSelector.getStationStats().pipe(take(1))
            .subscribe((i_stationStats) => {
                i_stationStats.forEach((_player) => {
                    if (_player.$.connection === "0") return;
                    if (_player.$.version !== "2") return;
                    const value = {
                        connection: "0",
                        playing: "0",
                        wdNative: "",
                        wdConnected: "",
                        wdExtension: "",
                        iotVer: "",
                        libVersion: "",
                        appVersion: "",
                        localIp: "",
                        platform: "",
                        lastUpdate: moment(Date.now()).format("MM-DD-YY HH:mm:ss"),
                    };
                    this.storeSelector.dispatchStaggered([actionStationV2SocketUpdate({
                        id: _player.$.id,
                        businessId: this.user.businessId,
                        version: "2",
                        value,
                    })], 0, 1);
                });
            });
    }

    public lockRecord(sceneId) {
        const saveRecord: ICRUDRecord = {
            studioId: this.localStoreService.getStorageConfig().studioId,
            command: "edit",
            primary: "player_data_id",
            table: "player_data",
            body: {},
            id: sceneId,
        };
        this.crudRecord(saveRecord).then((i_crudReply: ICRUDRecord) => {
            if (!i_crudReply.serverReply.status) {
                this.toastr.error(this.translocoService.translate("toast.failedSave", {}, this.translocoService.getActiveLang()));
            }
        });
    }

    /**
     * Upload File with Progress Tracking
     * Uploads files (resources, images, videos) to server with real-time progress updates.
     * Unlike regular crudRecord(), this method reports upload progress via callback.
     *
     * @param saveRecord - ICRUDRecord with:
     *   - command: "upload"
     *   - table: "resources"
     *   - body: FormData containing the file
     *   - options: { noStringify: true, reportProgress: true }
     *
     * @param _callback - Called on each progress update and completion
     *   Receives saveRecord with serverReply containing:
     *   - Progress: { type: "1", loaded: bytes, total: bytes }
     *   - Completion: { status: boolean, body: { id: resourceId } }
     *
     * Flow:
     * 1. Send file via HTTP POST with progress tracking
     * 2. On progress events: command = "progress", update percent
     * 3. On completion: command = "reply", status indicates success/failure
     * 4. Callback is invoked for each progress update
     *
     * Used by: uploadResourceFiles() for batch resource uploads
     */
    public async uploadFileWithProcess(saveRecord: ICRUDRecord, _callback = null) {
        return new Promise((resolve) => {
            this.userService.crudRecord(saveRecord)
                .pipe(
                    map((i_reply: ICRUDRecordReply) => {
                        if (i_reply && i_reply.hasOwnProperty("type") && i_reply.hasOwnProperty("status") == false) {
                            saveRecord.command = "progress";
                        } else {
                            saveRecord.command = "reply";
                        }
                        saveRecord.serverReply = i_reply;
                        return saveRecord;
                    }),
                    catchError((i_error) => {
                        return of(actionCRUDRecordFailure({
                            saveRecord: {
                                studioId: null,
                                command: "reply",
                                body: i_error,
                                serverReply: { status: false, id: null },
                            },
                        }));
                    }),
                ).subscribe((result) => {
                if (_callback) _callback(result);
            });
        });
    }

    /**
     * Set Uploading Status
     *
     * Updates the upload progress bar in the UI.
     *
     * @param status - Status object with { percent: number, show: boolean }
     *                 If null, calculates from files parameter
     * @param files - Array of FileObj to calculate aggregate progress
     *
     * If files array is provided, calculates total progress by:
     * - uploadedSize = sum of (fileSize * progress) for all files
     * - totalSize = sum of all file sizes
     * - percent = uploadedSize / totalSize
     */
    public setUploadingStatus(status = null, files = null) {
        if (!status) {
            if (!files) return;

            let uploadedSize = 0;
            let totalSize = 0;

            for (const fileObj of files) {
                uploadedSize += fileObj.fileSize * fileObj.progress;
                totalSize += fileObj.fileSize;
            }

            this.uploadingStatus.next({
                percent: Math.round(uploadedSize / totalSize),
                show: true,
            });
        } else {
            this.uploadingStatus.next(status);
        }
    }

    public getUploadingStatus(): Observable<any> {
        return this.uploadingStatus;
    }

    public setUploadingFiles(files: FileObj[] = []) {
        this.uploadingFiles.next(files);

        if (this.uploadingFiles.value.length == 0) {
            this.setUploadingStatus({
                percent: 0,
                show: false,
            });
        }
    }

    private m_wizardUpgradeRef: MatDialogRef<WizardUpgradeComponent> | null = null;
    /** true while the wizard chunk is being fetched, so a double click cannot open two wizards */
    private m_wizardUpgradeOpening = false;

    public openWizardUpgradeDialog(toast: EnumWizardUpgradeToasts = null, height = "800px", width = "800px", showCloseButton = true, enterpriseOnly = false, showFree = false): void {
        // Feature gates (everything except the generic account-upgrade buttons, which
        // pass showFree): show the renew/billing warning instead of the wizard ONLY when
        // the account already has the required plan but is inactive. If it's inactive AND
        // on the wrong plan (e.g. an inactive Plus hitting an Enterprise-only feature),
        // it still needs to upgrade — fall through to the upgrade wizard.
        if (!showFree && Lib.IsAccessBlocked(this.user)) {
            const ver = Lib.GetAccountVersion(this.user);
            // enterpriseOnly feature needs an Enterprise-tier account; plus-or-enterprise
            // features are satisfied by any paid (blocked => plus/ent/dash) account.
            const hasRequiredPlan = enterpriseOnly
                ? (ver === "ENTERPRISE_08" || ver === "ENTERPRISE_24")
                : true;
            if (hasRequiredPlan) {
                this.showAccountInactiveWarning();
                return;
            }
        }
        // Guard against stacking: some callers (e.g. table button bars) fire the gate
        // more than once per click, which would open multiple upgrade wizards.
        if (this.m_wizardUpgradeRef || this.m_wizardUpgradeOpening) {
            return;
        }
        // Standard callers pass "800px"; give the wizard a bit more breathing room
        // so the feature columns don't get cramped. Custom widths are left as-is.
        const dialogWidth = width === "800px" ? "920px" : width;
        // lazy: the wizard (prices, plans, icons) is fetched on first use instead of riding in
        // the shell; busyService.run shows the global loader while it downloads
        this.m_wizardUpgradeOpening = true;
        this.busyService.run(() => import("../containers/user/wizard-upgarde/wizard-upgrade.component")).then(({ WizardUpgradeComponent }) => {
            this.m_wizardUpgradeRef = this.dialog.open(WizardUpgradeComponent, {
                height,
                width: dialogWidth,
                disableClose: true,
                data: { toast, showCloseButton, enterpriseOnly, showFree },
            });
            this.m_wizardUpgradeRef.afterClosed().subscribe(() => {
                this.m_wizardUpgradeRef = null;
            });
        }).catch((e) => {
            console.error("failed to load upgrade wizard", e);
        }).finally(() => {
            this.m_wizardUpgradeOpening = false;
        });
    }

    public getUploadingFiles(): Observable<FileObj[]> {
        return this.uploadingFiles;
    }

    /**
     * Upload Resource Files
     *
     * Main method for batch uploading multiple resource files (images, videos, etc.)
     * to the server. Handles progress tracking, retry logic, and status updates.
     *
     * Flow:
     * 1. Show upload progress bar (percent: 0, show: true)
     * 2. Filter files with status READY or FAILED (skip already uploaded)
     * 3. For each file:
     *    - Create FormData with file
     *    - Create CRUD record with upload command
     *    - Call uploadFileWithProcess() with progress callback
     * 4. Progress callback updates fileObj with:
     *    - progress: 0-100 percent
     *    - uploadStatus: INPROGRESS -> SUCCESS or FAILED
     *    - fileId: Server-assigned resource ID on success
     * 5. Update aggregate upload status after each file progress update
     *
     * Upload Statuses (UploadStatus enum):
     * - READY: File queued for upload
     * - INPROGRESS: Currently uploading
     * - SUCCESS: Upload completed successfully
     * - FAILED: Upload failed (can be retried)
     *
     * Used by: Resource management UI for drag-and-drop file uploads
     */
    public uploadResourceFiles() {
        this.setUploadingStatus({
            percent: 0,
            show: true,
        });

        const validUploadFiles = this.uploadingFiles.value.filter((fileObj) =>
            fileObj.uploadStatus == UploadStatus.READY || fileObj.uploadStatus == UploadStatus.FAILED,
        );

        for (const fileObj of validUploadFiles) {

            const file: File = fileObj.file;
            const formData = new FormData();
            formData.append("thumbnail", file);

            const saveRecord: ICRUDRecord = {
                studioId: this.localStoreService.getStorageConfig().studioId,
                command: "upload",
                primary: "resource_id",
                table: "resources",
                body: formData,
                id: null,
                options: { noStringify: true, reportProgress: true },
            };

            this.uploadFileWithProcess(saveRecord, (i_crudReply) => {
                const reply = i_crudReply.serverReply;
                if (i_crudReply.command == "progress" || (reply.hasOwnProperty("type") && reply.hasOwnProperty("body") == false)) {
                    if (reply["type"] == "1" && reply["total"]) {
                        const percent = Math.round(100 * parseFloat(reply["loaded"]) / parseFloat(reply["total"]));
                        if (percent < 100) {
                            fileObj.progress = percent;
                        } else {
                            fileObj.progress = 100;
                        }
                    }
                    fileObj.uploadStatus = UploadStatus.INPROGRESS;
                } else {
                    if (!reply.status) {
                        fileObj.progress = 0;
                        fileObj.uploadStatus = UploadStatus.FAILED;
                    } else {
                        fileObj.progress = 100;
                        fileObj.uploadStatus = UploadStatus.SUCCESS;
                        if (reply["body"] && reply["body"].id) {
                            fileObj.fileId = reply["body"].id;
                        }
                    }
                }
                this.setUploadingStatus(null, validUploadFiles);
                this.setUploadingFiles(this.uploadingFiles.getValue());
            });
        }
    }
}


